import dynamic from 'next/dynamic';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { Button } from '@tranquility-ai/theme-components/button';
import { mediaIconButtonProps } from './media-icon-button';

import React, { useRef, useEffect, useState, useContext, useMemo } from 'react';
import { ListMagnifyingGlassIcon, XCircleIcon, FileCodeIcon, FileTxtIcon, FilePdfIcon, SpinnerGapIcon } from '@phosphor-icons/react';
import { encodeEvidenceLockerStyleProxyUriSegment, extractPathAfterBulk, FetchWithVersionCheck, formatTimeIndex, isTranscriptJsonLocation } from '@/shared/functions';
import { MediaSource } from '@/shared/types';
import { AuthContext } from '@/app/contexts';
import { useTimePilotT } from '@/i18n/useTimePilotT';

interface Source {
  sourceFilePath: string;
}

const speakerColors = [
  '#E69F00', // Orange
  '#56B4E9', // Sky Blue
  '#009E73', // Green
  '#F0E442', // Yellow
  '#0072B2', // Deep Blue
  '#D55E00', // Vermilion
  '#CC79A7', // Pink/Purple
  '#999999', // Light Gray
  '#882255', // Dark Red/Purple
  '#44AA99', // Teal
  '#117733', // Forest Green
  '#DDCC77'  // Mustard Yellow
];

function getSpeakerColor(speakerIndex: number): string {
  return speakerColors[speakerIndex % speakerColors.length];
}

function isVerticalScrollport(node: HTMLElement): boolean {
  const style = getComputedStyle(node);
  const oy = style.overflowY;
  if (oy !== 'auto' && oy !== 'scroll' && oy !== 'overlay') {
    return false;
  }
  if (node.hasAttribute('data-source-card-scrollport')) {
    return true;
  }
  const maxH = style.maxHeight;
  if (maxH && maxH !== 'none') {
    const parsed = Number.parseFloat(maxH);
    if (Number.isFinite(parsed) && parsed > 0) return true;
  }
  const h = style.height;
  if (h && h !== 'auto') {
    const parsed = Number.parseFloat(h);
    if (Number.isFinite(parsed) && parsed > 0) return true;
  }
  return node.scrollHeight > node.clientHeight + 1;
}

/**
 * Scrollports to drive during below-transcript grip resize, innermost first.
 *
 * Answer-item stops *before* ``[data-answer-item-scrollport]`` and never includes
 * it — the grip must scroll the media column, not grow/jump the answer row.
 * Elsewhere (Evidence Locker / question-bar preview) the marked source-card
 * scrollport is the outer bound so the page behind an overlay stays put.
 */
function collectVerticalScrollParents(el: HTMLElement | null): HTMLElement[] {
  const parents: HTMLElement[] = [];
  const answerItem = el?.closest('[data-answer-item-scrollport]') as HTMLElement | null;
  let node = el?.parentElement ?? null;
  while (node && node !== document.documentElement) {
    if (node === answerItem) break;
    if (isVerticalScrollport(node)) {
      parents.push(node);
    }
    if (!answerItem && node.hasAttribute('data-source-card-scrollport')) {
      break;
    }
    node = node.parentElement;
  }
  return parents;
}

function getResizeAnchorBottom(panel: HTMLElement, handleEl?: HTMLElement | null): number {
  const handleRoot = handleEl?.closest('.resize-ns') as HTMLElement | null;
  if (handleRoot) {
    return handleRoot.getBoundingClientRect().bottom;
  }
  if (handleEl) {
    return handleEl.getBoundingClientRect().bottom;
  }
  const handle = panel.querySelector('.resize-ns') as HTMLElement | null;
  return handle
    ? handle.getBoundingClientRect().bottom
    : panel.getBoundingClientRect().bottom;
}

/** Keep the resize handle (panel bottom) and pointer visible while the panel grows. */
function scrollParentsToFollowResize(
  panel: HTMLElement,
  clientY: number,
  options?: {
    scrollParents?: HTMLElement[];
    handleEl?: HTMLElement | null;
  },
) {
  const scrollParents = options?.scrollParents ?? collectVerticalScrollParents(panel);
  if (scrollParents.length === 0) return;

  const margin = 32;

  // Innermost → outermost, driven purely by how far the handle sits past each
  // port's edge. Remeasuring per port is what prevents nested ports from both
  // absorbing the same growth; once an inner port brings the handle back into
  // view the outer deltas resolve to 0 on their own.
  for (const scrollParent of scrollParents) {
    void scrollParent.scrollHeight;
    const rect = scrollParent.getBoundingClientRect();
    const anchorBottom = getResizeAnchorBottom(panel, options?.handleEl);
    let delta = 0;
    if (anchorBottom > rect.bottom - margin) {
      delta = anchorBottom - (rect.bottom - margin);
    }
    if (clientY > rect.bottom - margin) {
      delta = Math.max(delta, clientY - (rect.bottom - margin));
    } else if (clientY < rect.top + margin) {
      delta = Math.min(delta, clientY - (rect.top + margin));
    }
    if (delta === 0) continue;
    scrollParent.scrollTop += delta;
  }
}

function padZero(str: string, len?: number) {
  const zeros = new Array(len || 2).join('0');
  return (zeros + str).slice(-(len || 2));
}

function invertColor(hex: string, bw: boolean = true): string {
  if (hex.indexOf('#') === 0) {
    hex = hex.slice(1);
  }
  if (hex.length === 3) {
    hex = hex.split('').map(x => x + x).join('');
  }
  if (hex.length !== 6) {
    throw new Error('Invalid HEX color.');
  }
  let r = parseInt(hex.slice(0, 2), 16),
      g = parseInt(hex.slice(2, 4), 16),
      b = parseInt(hex.slice(4, 6), 16);
  if (bw) {
    return (r * 0.299 + g * 0.587 + b * 0.114) > 186
      ? '#000000'
      : '#FFFFFF';
  }
  r = 255 - r;
  g = 255 - g; 
  b = 255 - b;
  return `#${padZero(r.toString(16))}${padZero(g.toString(16))}${padZero(b.toString(16))}`;
}

interface TranscriptProps {
  mediaItem: MediaSource;
  transcriptRef: React.RefObject<HTMLDivElement>;
  transcript: any[];
  filteredTranscript: any[];
  activeSegmentIndex: number;
  handleSegmentClick: (startTime: number) => void;
  searchQuery: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isSearchInputVisible: boolean;
  toggleSearchInputVisibility: () => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  transcriptSrc?: string;
  handleDownload: (source: Source) => void;
  position: 'side' | 'below';
  isPortrait: boolean;
  playerHeight: number;
  playerWidth: number;
  style?: React.CSSProperties;
  autoScroll?: boolean;
  isFullscreen?: boolean;
  /** Side transcript may grow past 500px (fullscreen video zoom-out). */
  expandSideWidth?: boolean;
  /** Clears a between-panel split lock so bottom drag can grow the stack. */
  onBelowTranscriptResizeStart?: () => void;
  /** Notifies parent of the final panel height so soft-fit budget can grow with it. */
  onBelowTranscriptResizeEnd?: (heightPx: number) => void;
}

interface Speaker {
  bg: string;
  fg: string;
}

const calculateHeaderHeight = (text: string) => {
  const charsPerLine = 77;
  const lines = Math.ceil((text || '').length / charsPerLine);
  return Math.max((lines * 20), 20) + 10;
}

const calculateItemHeight = (text: string) => {
  const charsPerLine = 92;
  const lines = Math.ceil((text || '').length / charsPerLine);
  return Math.max((lines * 16), 26) + 6;
}

/** Innermost overflow scroll ancestor of `el` within `boundary` (does not walk past the document). */
function findInnermostScrollContainer(el: HTMLElement, boundary: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && boundary.contains(node)) {
    const { overflowY } = getComputedStyle(node);
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight;
    if (canScroll) return node;
    node = node.parentElement;
  }
  const { overflowY } = getComputedStyle(boundary);
  const boundaryScrolls =
    (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
    boundary.scrollHeight > boundary.clientHeight;
  return boundaryScrolls ? boundary : null;
}

const Transcript: React.FC<TranscriptProps> = ({
  mediaItem,
  transcriptRef,
  transcript,
  filteredTranscript,
  activeSegmentIndex,
  handleSegmentClick,
  searchQuery,
  handleSearchChange,
  isSearchInputVisible = true,
  toggleSearchInputVisibility,
  searchInputRef,
  transcriptSrc,
  handleDownload,
  position,
  isPortrait,
  playerHeight,
  playerWidth,
  style,
  autoScroll,
  isFullscreen = false,
  expandSideWidth = false,
  onBelowTranscriptResizeStart,
  onBelowTranscriptResizeEnd,
}) => {
  const t = useTimePilotT();
  const [speakers, setSpeakers] = useState<Record<string, Speaker> | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([]);
  
  /** Below/SourceCard: chrome (title/search/speakers) + first line + drag handle. */
  const DEFAULT_BELOW_TRANSCRIPT_HEIGHT_PX = 240;
  const MIN_BELOW_TRANSCRIPT_HEIGHT_PX = 180;
  const [transcriptHeight, setTranscriptHeight] = useState<number>(
    position === 'side' ? 400 : DEFAULT_BELOW_TRANSCRIPT_HEIGHT_PX,
  );
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [displayedTranscript, setDisplayedTranscript] = useState<any[]>([]);

  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const exportPdfPagesRef = useRef<HTMLDivElement>(null);
  const exportPdfLinkRef = useRef<HTMLAnchorElement>(null);

  const { idToken, accessToken } = useContext(AuthContext);

  useEffect(() => {
    const speakers: Record<string, Speaker> = {};
    for (const item of filteredTranscript) {
      if (!speakers[item.speaker]) {
        const colorCount = Object.keys(speakers).length + 1;
        const color = getSpeakerColor(colorCount);
        speakers[item.speaker] = {
          bg: color,
          fg: invertColor(color, true) // true for black/white text
        }
      }
    }
    if (Object.keys(speakers).length > 0) {
      setSpeakers(speakers);
    }
  }, [filteredTranscript]);

  useEffect(() => {
    const displayedTranscript = selectedSpeakers && selectedSpeakers.length > 0
      ? filteredTranscript.filter(item => selectedSpeakers.includes(item.speaker))
      : filteredTranscript;
    setDisplayedTranscript(displayedTranscript);
  }, [selectedSpeakers.length, filteredTranscript]);

  const activeDisplayIndex = useMemo(() => {
    if (activeSegmentIndex < 0) return -1;
    const active = filteredTranscript[activeSegmentIndex];
    if (!active) return -1;
    return displayedTranscript.findIndex(
      (item) => item.start === active.start && item.end === active.end,
    );
  }, [activeSegmentIndex, filteredTranscript, displayedTranscript]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Backspace') {
        handleSearchChange({ 
          target: {
            value: ''
          } 
        } as React.ChangeEvent<HTMLInputElement>);
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSearchChange, searchInputRef]);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // Adjust the timeout as needed

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!autoScroll || activeDisplayIndex < 0) return;
    const el = segmentRefs.current[activeDisplayIndex];
    const boundary = transcriptRef.current;
    if (!el || !boundary || !boundary.contains(el)) return;

    const scrollContainer = findInnermostScrollContainer(el, boundary);
    if (!scrollContainer) return;

    const elRect = el.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const isVisible = elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom;
    if (!isVisible) {
      const deltaY =
        elRect.top - containerRect.top - scrollContainer.clientHeight / 2 + elRect.height / 2;
      scrollContainer.scrollBy({ top: deltaY, behavior: 'smooth' });
    }
  }, [activeDisplayIndex, autoScroll, transcriptRef]);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      event.stopPropagation();
    };

    const transcriptElement = transcriptRef.current;
    transcriptElement?.addEventListener('scroll', handleScroll);

    return () => {
      transcriptElement?.removeEventListener('scroll', handleScroll);
    };
  }, [transcriptRef]);

  const handleResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    window.getSelection()?.removeAllRanges();
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    const startY = e.clientY;
    const startHeight =
      transcriptRef.current?.getBoundingClientRect().height || transcriptHeight;
    const minHeight =
      position === 'below' ? MIN_BELOW_TRANSCRIPT_HEIGHT_PX : 200;

    // Captured once so the compensation below reads the same ports every frame.
    const dragScrollParents = collectVerticalScrollParents(transcriptRef.current);
    const sumScrollTop = () =>
      dragScrollParents.reduce((total, el) => total + el.scrollTop, 0);
    const startScrollTop = sumScrollTop();

    /**
     * Scrolling a parent to keep the grip in view slides the panel up underneath the
     * cursor, so pointer travel alone understates the drag by however far we scrolled.
     * Without adding that back the grip falls behind the pointer and expansion stops
     * dead once the pointer runs out of viewport.
     */
    const heightForPointer = (clientY: number) =>
      Math.max(
        minHeight,
        startHeight + (clientY - startY) + (sumScrollTop() - startScrollTop),
      );

    // Height that is actually on the panel right now, so an end without dependable
    // coordinates can commit what the user sees.
    let lastAppliedHeight = startHeight;

    const applyHeight = (newHeight: number, clientY: number) => {
      lastAppliedHeight = newHeight;
      const panel = transcriptRef.current;
      // Sync DOM height before scroll so SourceCard layout grows in this frame.
      if (panel) {
        panel.style.height = `${newHeight}px`;
        // Nudge minHeight too so flex ancestors recompute scrollHeight immediately.
        panel.style.minHeight = `${newHeight}px`;
      }
      setTranscriptHeight(newHeight);
      if (panel) {
        const followOpts = { scrollParents: dragScrollParents, handleEl: target };
        scrollParentsToFollowResize(panel, clientY, followOpts);
        // After layout (parents grow), scroll again so the handle stays in view.
        requestAnimationFrame(() => {
          const live = transcriptRef.current;
          if (!live) return;
          scrollParentsToFollowResize(live, clientY, followOpts);
          requestAnimationFrame(() => {
            const latest = transcriptRef.current;
            if (!latest) return;
            scrollParentsToFollowResize(latest, clientY, followOpts);
          });
        });
      }
    };

    const onPointerMove = (moveEvent: PointerEvent) => {
      applyHeight(heightForPointer(moveEvent.clientY), moveEvent.clientY);
    };

    const endDrag = (pointerId: number, finalHeight: number) => {
      // Cancel has already dropped capture implicitly, and releasing a pointer that is
      // no longer active throws — which would strand the listeners below.
      if (target.hasPointerCapture(pointerId)) {
        target.releasePointerCapture(pointerId);
      }
      if (position === 'below') {
        onBelowTranscriptResizeEnd?.(finalHeight);
      }
      target.removeEventListener('pointermove', onPointerMove);
      target.removeEventListener('pointerup', onPointerUp);
      target.removeEventListener('pointercancel', onPointerCancel);
    };

    const onPointerUp = (upEvent: PointerEvent) => {
      const finalHeight = heightForPointer(upEvent.clientY);
      applyHeight(finalHeight, upEvent.clientY);
      endDrag(upEvent.pointerId, finalHeight);
    };

    /**
     * Touch interrupt or browser gesture takeover. `pointercancel` coordinates are not
     * guaranteed to be meaningful, so commit the last height already applied rather
     * than recomputing a new one from the event — and commit it, because
     * `onBelowTranscriptResizeEnd` is what adopts the stack as the soft-fit budget.
     */
    const onPointerCancel = (cancelEvent: PointerEvent) => {
      endDrag(cancelEvent.pointerId, lastAppliedHeight);
    };

    target.addEventListener('pointermove', onPointerMove);
    target.addEventListener('pointerup', onPointerUp);
    target.addEventListener('pointercancel', onPointerCancel);
  };

  const generatePdfOnServer = async () => {
    setGeneratingPdf(true);
    try {
      const transcriptFilename = extractPathAfterBulk(mediaItem.s3Url || mediaItem.mediaUrl || '/', true).truncatedFilePath;
      const downloadFilename = `${transcriptFilename && transcriptFilename !== '' ? `${transcriptFilename}.` : ''}transcript.pdf`;
      const body = {
        documentKind: 'transcript',
        documentData: {
          transcriptFilename: transcriptFilename,
          transcriptData: displayedTranscript,
        }
      };
      const exportPdfPath = '/api/protected/exportPdf';
      let rawResult = await FetchWithVersionCheck(exportPdfPath, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        // if wanting to scope, populate files/scopes/excludedFiles/excludedScopes
        body: JSON.stringify(body)
      });
      const result = await rawResult.arrayBuffer();
      let pdfUrl = '';
      if (result) {
        // use application/download to force a download
        const pdfBlob = new Blob([result], { type: 'application/pdf' });
        pdfUrl = URL.createObjectURL(pdfBlob);
      }
      exportPdfLinkRef.current!.download = downloadFilename;
      exportPdfLinkRef.current!.href = pdfUrl;
      exportPdfLinkRef.current!.click();
    } catch (error) {
      // toast error
    }
    setGeneratingPdf(false);
  }

  const getTranscript = async (transcriptSrc: string) => {
    if (!transcriptSrc || transcriptSrc.trim() === '' || !isTranscriptJsonLocation(transcriptSrc)) return;
    try {
      let response = null;
      let apiUrl = `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(transcriptSrc)}`;
      response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error loading transcript:', error);
      return undefined;
    }
  }

  const handleDownloadTranscriptSource = async () => {
    const mediaFilename = (mediaItem.s3Url || mediaItem.mediaUrl || '/').split('/').pop();
    const transcriptFilename = `${mediaFilename && mediaFilename !== '' ? `${mediaFilename}.` : ''}transcript.json`;
    if (!transcriptSrc || transcriptSrc.trim() === '') return;
    const transcriptResponse = await getTranscript(transcriptSrc);
    if (!transcriptResponse) return;
    const transcript = await transcriptResponse.json();
    const blob = new Blob([JSON.stringify(transcript)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = transcriptFilename;
    a.click();
    URL.revokeObjectURL(url);    
  }

  const handleDownloadAsText = async () => {
    const mediaFilename = (mediaItem.s3Url || mediaItem.mediaUrl || '/').split('/').pop();
    const transcriptFilename = `${mediaFilename && mediaFilename !== '' ? `${mediaFilename}.` : ''}transcript.txt`;
    const fileDisplayName = extractPathAfterBulk(mediaItem.s3Url || mediaItem.mediaUrl || '/').truncatedFilePath;
    let text = mediaFilename ? `${t('media.transcriptFor', { name: fileDisplayName || '' })}\n\n` : '';
    for (const item of transcript) {
      text += `${item.speaker} (${formatTimeIndex(item.start.toString())}): ${item.text}\n`;
    }
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = transcriptFilename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Fullscreen side: fill MediaDisplay row height. SourceCard below: definite
  // drag-resize height (never let height:'auto' collapse the flex body).
  // Pull flex shorthand/longhands off the incoming style so we never mix
  // `flex` with `flexShrink` (React 19 warns and the cascade is undefined).
  const {
    flex: incomingFlex,
    flexGrow: incomingFlexGrow,
    flexShrink: incomingFlexShrink,
    flexBasis: incomingFlexBasis,
    ...styleRest
  } = style ?? {};
  const isFullscreenSide = isFullscreen && position === 'side';
  const mergedStyle: React.CSSProperties = isFullscreenSide
    ? {
        backgroundColor: 'rgba(0, 0, 0, .5)',
        display: 'flex',
        flexDirection: 'column',
        resize: 'none',
        ...styleRest,
        // Let flex stretch fill the MediaPlayer row (not a % of a content-sized parent).
        height: 'auto',
        maxHeight: 'none',
        minHeight: 0,
        alignSelf: 'stretch',
        flex: incomingFlex ?? '1 1 0',
      }
    : {
        backgroundColor: 'rgba(0, 0, 0, .5)',
        display: 'flex',
        flexDirection: 'column',
        resize: 'none',
        ...styleRest,
        height:
          style?.height != null && style.height !== 'auto'
            ? style.height
            : `${transcriptHeight}px`,
        minHeight:
          style?.minHeight != null
            ? style.minHeight
            : MIN_BELOW_TRANSCRIPT_HEIGHT_PX,
        maxHeight: 'none',
        ...(incomingFlex != null
          ? { flex: incomingFlex }
          : {
              flexGrow: incomingFlexGrow ?? 0,
              flexShrink: incomingFlexShrink ?? 0,
              flexBasis: incomingFlexBasis ?? 'auto',
            }),
      };

  return (
    <div
      className={`box-border rounded-lg w-full min-w-[245px] relative custom-scrollbar2
        z-100 overflow-hidden border-1 border-b-3 border-gray-700 border-solid flex flex-col ${
        isFullscreenSide ? 'min-h-0 p-3 pb-1' : 'min-h-[180px] p-3'
      } ${
        position === 'side'
          ? expandSideWidth
            ? 'min-w-0 max-w-none flex-1'
            : 'max-w-[500px]'
          : 'w-full min-w-0'
      }`}
      ref={transcriptRef}
      style={mergedStyle}
    >
      <div className='export-transcript hidden overflow-scroll'>
        <div ref={exportPdfPagesRef} className='flex flex-col gap-1'>
        </div>
      </div>
      {/* Chrome: shrink-0 so search / Speakers never stack onto each other. */}
      <div className="box-border flex w-full shrink-0 flex-col gap-1.5 pb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-base font-light tracking-wider m-0 p-0 flex items-center gap-1 text-white whitespace-nowrap flex-shrink-0">
            {t('media.transcript')}
          </label>
          <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
            {transcriptSrc && (
              <TpTooltip content={t('media.downloadJson')} placement="top" delayDuration={700}>
                <Button
                  type="button"
                  {...mediaIconButtonProps}
                  aria-label={t('media.downloadJson')}
                  onClick={(event) => {
                    handleDownloadTranscriptSource();
                    // Drop click-focus so the tooltip can dismiss on pointer leave (TR-2599).
                    event.currentTarget.blur();
                  }}
                >
                  <FileCodeIcon size={20} />
                </Button>
              </TpTooltip>
            )}
            {(transcriptSrc && transcript.length > 0) && (
              <TpTooltip content={t('media.exportText')} placement="top" delayDuration={700}>
                <Button
                  type="button"
                  {...mediaIconButtonProps}
                  aria-label={t('media.exportText')}
                  onClick={(event) => {
                    handleDownloadAsText();
                    event.currentTarget.blur();
                  }}
                >
                  <FileTxtIcon size={20} />
                </Button>
              </TpTooltip>
            )}
            {(transcriptSrc && filteredTranscript.length > 0) && (
              <>
                <TpTooltip content={t('media.exportPdf')} placement="top" delayDuration={700}>
                  <Button
                    type="button"
                    {...mediaIconButtonProps}
                    aria-label={t('media.exportPdf')}
                    onClick={(event) => {
                      generatePdfOnServer();
                      event.currentTarget.blur();
                    }}
                    disabled={generatingPdf}
                  >
                    {generatingPdf ? <SpinnerGapIcon size={20} className='animate-spin-slow' /> : <FilePdfIcon size={20} />}
                  </Button>
                </TpTooltip>
                <a className='opacity-0 h-[1px] w-[1px] overflow-hidden' ref={exportPdfLinkRef} href={""} download="transcript.pdf"></a>
              </>
            )}
          </div>
        </div>
        <div className="box-border flex flex-row items-center rounded-md cursor-pointer border-1 border-white/20 border-solid transition-all duration-300 gap-1 w-full pl-1 py-0.5 shrink-0">
          <ListMagnifyingGlassIcon className="w-5 h-5 flex-shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={searchQuery ? '' : ''}
            className="box-border flex flex-grow w-[40px] outline-none placeholder-gray-300 text-gray-300 bg-white/0 border-none tracking-wide font-lighter overflow-hidden"
          />
          {searchQuery && (
            <div className='flex items-center gap-1'>
              <XCircleIcon
                size={20}
                weight='fill'
                className='mr-1 text-white/80 hover:text-red-500 transition-all duration-1000 cursor-pointer'
                onClick={() => {
                  handleSearchChange({
                    target: {
                      value: ''
                    }
                  } as React.ChangeEvent<HTMLInputElement>);
                  searchInputRef.current?.focus();
                }}
              />
            </div>
          )}
        </div>
        {speakers && (
          <div className="flex flex-wrap items-center justify-start gap-2 border-1 border-white/5 border-solid rounded-sm px-2 py-1 bg-white/5 shrink-0">
            <label className="text-white/90 text-sm shrink-0">{t('media.speakers')}</label>
            {Object.keys(speakers || {}).map((speaker) => {
              const speakerStyle = speakers?.[speaker]?.bg || 'gray';
              const speakerLabel = speaker === "undefined" ? t('media.unidentified') : speaker;
              return (
                <div
                  key={speaker}
                  className={`px-1 py-0.5 rounded-sm cursor-pointer ${selectedSpeakers.length > 0 && selectedSpeakers.includes(speaker) ? 'border-2 border-white' : ''}`}
                  style={{
                    backgroundColor: speakerStyle,
                    opacity: selectedSpeakers.length === 0 || selectedSpeakers.includes(speaker) ? 1 : 0.5
                  }}
                  onClick={() => {
                    if (Object.keys(speakers).length > 1) {
                      setSelectedSpeakers(selectedSpeakers.includes(speaker) ? selectedSpeakers.filter((speak: string) => speak !== speaker) : [ ...selectedSpeakers, speaker ]);
                    }
                  }}
                >
                  <div
                    className="font-medium tracking-wider text-sm"
                    style={{ color: invertColor(speakerStyle) }}
                  >
                    {speakerLabel}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Scrollable lines — flex-1 fills leftover panel height (no 70vh cap). */}
      <div
        className={`min-h-0 flex-1 overflow-y-auto pr-2 space-y-1 text-sm text-left transcript-container custom-scrollbar2 max-h-none ${
          isFullscreenSide ? 'pb-1' : 'pb-3'
        }`}
      >        {displayedTranscript.length > 0 ? (
          displayedTranscript.map((item, index) => (
            <div
              key={index}
              ref={(el) => {
                segmentRefs.current[index] = el;
                return undefined;
              }}
              onClick={() => handleSegmentClick(item.start)}
              className={`flex items-start p-2 rounded-md transition-colors cursor-pointer relative z-0 ${
                index === activeDisplayIndex
                  ? 'bg-white/20 text-white border-1 border-white/20 border-solid'
                  : 'hover:bg-gray-700 text-gray-300 border-1 border-white/0 border-solid'
              }`}
            >
              {speakers && (
                <TpTooltip
                  content={
                    <div 
                    className="px-1 py-0.5 rounded-sm font-medium"
                    style={{ 
                    backgroundColor: speakers?.[item.speaker]?.bg || 'black', 
                    color: speakers?.[item.speaker]?.fg || 'white',
                    visibility: Object.keys(speakers).length > 1 ? 'visible' : 'hidden'
                    }}
                    >
                    {item.speaker === "undefined" ? t('media.unidentified') : item.speaker}
                    </div>
                  }
                  sideOffset={12}
                  delayDuration={300}
                  placement={'left'}
                >
                  <div
                    className={`flex items-center w-fit font-medium tracking-wider mr-2 rounded-sm px-1 py-0.5`}
                    style={{ backgroundColor: speakers[item.speaker]?.bg || 'black', color: speakers[item.speaker]?.fg || 'white' }}
                  >
                    {formatTimeIndex(item.start.toString())}
                  </div>
                </TpTooltip>
              )}
              <div className="mt-[2px] text-white font-extralight tracking-wide">
                {item.text}
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center text-white text-center w-full font-semibold tracking-wider">
            {isLoading ? <div className='flex items-center gap-2'><SpinnerGapIcon size={20} className='animate-spin-slow animate-pulse' /> <label className='text-white/80'>{t('media.transcriptsLoading')}</label></div> : searchQuery ? t('media.noResults') : ""}
          </div>
        )}
      </div>
      {/* Resize Handle — SourceCard / below only; fullscreen side fills MediaDisplay height. */}
      {!isFullscreenSide && (
        <div className='resize-ns'>
          <div
            onPointerDown={handleResizePointerDown}
            style={{ touchAction: 'none' }}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transcript; 